<!--
  src/components/SingleTopKeyword.vue
  v5.8：platform 固定 alibaba，店铺用 shop_code。禁止 platform=lotov。
-->
<template>
  <div class="single-top-keyword">
    <div class="controls">
      <el-input
        v-model="keyword"
        placeholder="请输入关键词（如：shoe）"
        style="width: 300px;"
        @keyup.enter="fetchData"
      />
      <el-select v-model="shopCode" style="width: 140px;" @change="fetchData">
        <el-option label="Lotov" value="lotov" />
        <el-option label="NeoPek" value="neopek" />
      </el-select>
      <el-select v-model="metric" style="width: 180px;">
        <el-option label="曝光量" value="impressions" />
        <el-option label="点击量" value="clicks" />
        <el-option label="点击率" value="ctr" />
      </el-select>
      <el-button type="primary" @click="fetchData">查询趋势</el-button>
    </div>

    <div v-if="hasData" ref="chartRef" class="chart"></div>

    <div v-else class="empty-tip">
      <el-empty :description="emptyDescription" />
    </div>

    <el-table
      v-if="hasData"
      :data="tableData"
      style="width: 100%; margin-top: 24px;"
      border
      :default-sort="{ prop: 'year', order: 'descending' }"
    >
      <el-table-column prop="year" label="年" width="80" sortable />
      <el-table-column prop="week" label="周" width="100" sortable>
        <template #default="{ row }">
          {{ weekLabel(row) }}
        </template>
      </el-table-column>
      <el-table-column prop="day" label="结束日" width="120">
        <template #default="{ row }">{{ row.day || '-' }}</template>
      </el-table-column>
      <el-table-column prop="impressions" label="曝光量" sortable width="120" />
      <el-table-column prop="clicks" label="点击量" sortable width="120" />
      <el-table-column label="点击率" width="120">
        <template #default="{ row }">
          {{ row.impressions > 0 ? ((Number(row.ctr) || 0) * 100).toFixed(2) + '%' : '0.00%' }}
        </template>
      </el-table-column>
      <el-table-column prop="p4p_impressions" label="直通车曝光" width="130" />
      <el-table-column prop="p4p_clicks" label="直通车点击" width="130" />
      <el-table-column prop="p4p_promotion_duration" label="推广时长(h)" width="140" />
      <el-table-column label="Top10平均曝光" width="140">
        <template #default="{ row }">{{ row.impressions_avg_top10 || 0 }}</template>
      </el-table-column>
      <el-table-column label="Top10平均点击" width="140">
        <template #default="{ row }">{{ row.clicks_avg_top10 || 0 }}</template>
      </el-table-column>
      <el-table-column prop="keyword_index" label="关键词指数" width="130" />
      <el-table-column prop="seller_scale_index" label="卖家规模指数" width="140" />
    </el-table>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, computed, onUnmounted, onMounted } from 'vue'
import * as echarts from 'echarts'
import { ElMessage } from 'element-plus'
import request from '@/utils/axios'

const PLATFORM = 'alibaba'
const SHOP_NAMES = new Set(['lotov', 'neopek'])

const props = defineProps({
  initialKeyword: { type: String, default: '' },
  initialPlatform: { type: String, default: 'alibaba' },
  initialShopCode: { type: String, default: 'lotov' }
})

function resolveShop(platform, shopCode) {
  const shop = String(shopCode || '').trim().toLowerCase()
  const rawPlatform = String(platform || '').trim().toLowerCase()
  if (SHOP_NAMES.has(shop)) return shop
  if (SHOP_NAMES.has(rawPlatform)) return rawPlatform
  return 'lotov'
}

const keyword = ref(props.initialKeyword)
const shopCode = ref(resolveShop(props.initialPlatform, props.initialShopCode))
const metric = ref('impressions')
const tableData = ref([])
const chartRef = ref(null)
let myChart = null

const hasData = computed(() => tableData.value.length > 0)

const emptyDescription = computed(() => {
  return keyword.value
    ? `该关键词在 ${shopCode.value} 近10周暂无数据`
    : '请输入关键词并查询以查看趋势数据'
})

function weekLabel(row) {
  const week = Number(row.week) || 0
  return `${row.year}-W${String(week).padStart(2, '0')}`
}

function extractList(response) {
  const data = response?.data || response || (Array.isArray(response) ? response : [])
  if (Array.isArray(data)) return data
  if (Array.isArray(data.results)) return data.results
  return []
}

const fetchData = async () => {
  if (!keyword.value.trim()) {
    ElMessage.warning('请输入关键词')
    return
  }

  try {
    const response = await request.get('/topkeywords/trend/', {
      params: {
        keyword: keyword.value.trim(),
        platform: PLATFORM,
        shop_code: shopCode.value,
        n: 10
      }
    })

    tableData.value = extractList(response)

    if (tableData.value.length === 0) {
      ElMessage.info('未获取到数据')
      return
    }

    await nextTick()
    updateChart()
  } catch (error) {
    console.error('请求失败:', error)
    tableData.value = []
    const detail = error?.response?.data?.detail || error?.response?.data?.error || error?.message
    ElMessage.error(detail || '趋势数据加载失败')
  }
}

const updateChart = () => {
  if (!chartRef.value || tableData.value.length === 0) return
  if (!myChart) myChart = echarts.init(chartRef.value)

  const labels = tableData.value.map(weekLabel)

  const selfData = tableData.value.map(item => {
    if (metric.value === 'ctr') return item.impressions > 0 ? (Number(item.ctr) || 0) * 100 : 0
    return item[metric.value] ?? 0
  })

  const top10Data = tableData.value.map(item => {
    if (metric.value === 'impressions') return item.impressions_avg_top10 || 0
    if (metric.value === 'clicks') return item.clicks_avg_top10 || 0
    if (metric.value === 'ctr') {
      return item.impressions_avg_top10 > 0 ? (item.clicks_avg_top10 / item.impressions_avg_top10) * 100 : 0
    }
    return 0
  })

  const yAxisName = metric.value === 'ctr' ? '点击率 (%)' : metric.value === 'impressions' ? '曝光量' : '点击量'

  myChart.setOption({
    backgroundColor: '#f8f9fb',
    title: { text: `${keyword.value} / ${shopCode.value} - 近10周${yAxisName}趋势对比`, left: 'center', textStyle: { color: '#303133', fontSize: 16 } },
    tooltip: { trigger: 'axis', backgroundColor: 'rgba(255,255,255,0.95)', textStyle: { color: '#303133' } },
    legend: { data: ['自己店铺', 'Top10 平均'], top: 40, textStyle: { color: '#303133' } },
    grid: { top: 90, bottom: 60, left: 60, right: 40, containLabel: true },
    xAxis: {
      type: 'category',
      data: labels,
      axisLabel: { rotate: 45, color: '#606266', fontSize: 12 },
      axisLine: { lineStyle: { color: '#e8e8e8' } }
    },
    yAxis: {
      type: 'value',
      name: yAxisName,
      min: metric.value === 'ctr' ? 0 : null,
      axisLabel: { color: '#606266' },
      axisLine: { lineStyle: { color: '#e8e8e8' } },
      nameTextStyle: { color: '#606266' }
    },
    series: [
      {
        name: '自己店铺',
        type: 'line',
        data: selfData,
        smooth: true,
        itemStyle: { color: '#409eff' },
        lineStyle: { width: 3 }
      },
      {
        name: 'Top10 平均',
        type: 'line',
        data: top10Data,
        smooth: true,
        itemStyle: { color: '#67c23a' },
        lineStyle: { width: 3, type: 'dashed' }
      }
    ]
  }, true)
}

onMounted(() => {
  if (props.initialKeyword) {
    keyword.value = props.initialKeyword
    fetchData()
  }
})

watch(() => props.initialKeyword, (newVal) => {
  if (newVal) {
    keyword.value = newVal
    fetchData()
  }
})

watch(metric, () => {
  if (hasData.value) nextTick(updateChart)
})

onUnmounted(() => {
  if (myChart) {
    myChart.dispose()
    myChart = null
  }
})
</script>

<style scoped>
.single-top-keyword { padding: 24px; max-width: 1400px; margin: 0 auto; }
.controls { margin-bottom: 24px; display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.chart { width: 100%; height: 480px; background: #f8f9fb; border: 1px solid #e8e8e8; border-radius: 8px; margin-top: 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); }
.empty-tip { margin-top: 80px; text-align: center; }
</style>
